import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

console.log('🔧 API Configuration:', {
  baseURL: API_BASE_URL,
  environment: process.env.NODE_ENV,
  debug: process.env.REACT_APP_DEBUG
});

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: 30000
});

apiClient.interceptors.request.use(
  (config) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('🔵 API Request:', config.method.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('✅ API Response:', response.status, response.data);
    }
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.message);

    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);

      // 401 on login endpoint = wrong credentials, NOT session expiry
      // Only redirect to login for 401s on non-auth endpoints
      if (error.response.status === 401) {
        const isLoginEndpoint = error.config?.url?.includes('/auth/login.php');
        if (!isLoginEndpoint) {
          console.log('Session expired, redirecting to login...');
          localStorage.removeItem('ltms_session');
          localStorage.removeItem('sessionData');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (error.response.status === 403) {
        console.error('Access denied:', error.response.data.message);
      }

      // System-wide access window has closed. Every backend endpoint
      // returns this once it fires, so catching it once here covers
      // the whole app regardless of which call triggered it.
      if (error.response.status === 423 && error.response.data?.locked) {
        localStorage.removeItem('ltms_session');
        localStorage.removeItem('sessionData');
        window.location.href = '/system-unavailable.html';
        return Promise.reject(error);
      }

    } else if (error.request) {
      console.error('❌ No response received from server');
      console.error(`💡 Backend not reachable at ${API_BASE_URL}`);
    }

    return Promise.reject(error);
  }
);

export const loginUser = async (username, password) => {
  try {
    const response = await apiClient.post('/auth/login.php', { username, password });
    return response.data;
  } catch (error) {
    // Return a clean error object instead of throwing
    // so Login.jsx catch block gets a usable message
    return error.response?.data || {
      success: false,
      message: 'Network error - Cannot reach backend server. Please ensure the PHP server is running on port 8000.'
    };
  }
};

export const getFinancialYears = async (companyId) => {
  try {
    const response = await apiClient.get('/company/get-financial-years.php', {
      params: { companyid: companyId }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const createSession = async (userId, companyId, yearId) => {
  try {
    const response = await apiClient.post('/auth/create-session.php', {
      userid: userId,
      companyid: companyId,
      yearid: yearId
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const logoutUser = async () => {
  try {
    const response = await apiClient.post('/logout.php');
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const verifySession = async () => {
  try {
    const response = await apiClient.get('/verify-session.php');
    return response.data;
  } catch (error) {
    console.warn('⚠️ verifySession failed:', error.message);
    return { success: false, message: 'Session verification failed' };
  }
};

export const getUserMenus = async () => {
  try {
    const response = await apiClient.get('/menus.php');
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Failed to load menus' };
  }
};

export default apiClient;